import URI from 'jsuri';

import { useRouter } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { useRouterParams } from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useMiniModalRedirectData } from 'bundles/unified-home-common/contexts/enterprise/MiniModalRedirectContext';

const getProductType = (productVariantOrRouteParam?: string, isProject?: boolean) => {
  const productTypes = {
    course: 'learn',
    s12n: 'specializations',
    profS12n: 'professional-certificates',
    project: 'projects',
    ProfessionalCertificateS12n: 'professional-certificates',
    ExternalCertificateS12n: 'professional-certificates',
    NormalS12n: 'specializations',
    NORMAL_S12N: 'specializations',
    PROFESSIONAL_CERTIFICATE_S12N: 'professional-certificates',
  };

  const productType = isProject
    ? productTypes.project
    : productTypes[productVariantOrRouteParam as keyof typeof productTypes];
  return productType;
};

/*
Redirects the user to the XDP page if they come to enterprise home with query params used to show the old "XDP mini modal".
We use the EnterpriseModalManager context to determine if we should attempt to redirect them,
there are other conditions that can override this action. EnterpriseModalManager handles this logic.
*/
function useEnterpriseMiniModalRedirect() {
  // The existing useRouterParams function (called in 16 other places, as of this writing) takes a whole router object
  // and uses both its `params` and `location.query`, so we'll just request the whole router to prevent a large blast
  // radius in this PR.
  const router = useRouter();
  const shouldRedirect =
    useEnterpriseModalManager()?.modalToShow === ENTERPRISE_MODAL_NAMES.PRODUCT_DESCRIPTION_PAGE_REDIRECT;
  // Note: useRouterParams is not a Hook, despite the name.
  const routerParams = useRouterParams(router);
  const { isLoading, courseSlug, s12nSlug, s12nProductVariant, isProject } = useMiniModalRedirectData();

  // routerParams will attribute any project type as a course. Ensure we pass the correct path here.
  const productType = getProductType(s12nProductVariant || routerParams?.productType, isProject);
  const slug = courseSlug || s12nSlug;
  const xdpProps = routerParams?.productType && router.params.programSlug && slug;

  if (shouldRedirect && !isLoading && xdpProps && productType) {
    // Remove miniModal params so back navigation works
    const query = { ...(router?.location?.query || {}) };
    delete query.showMiniModal;
    Object.keys(routerParams).forEach((key) => delete query[key]);

    router.replace({
      pathname: router?.location?.pathname,
      query,
    });

    // Some query params we want to shed and some we'd like to keep. Params in this list will be retained.
    const queryParamsToRetain = new Set([
      'deepLinkItemId',
      'fromClip',
      'collectionId',
      'source',
      'isExternal',
      'attemptSSOLogin',
    ]);
    const redirectUrl = new URI(`/programs/${router.params.programSlug}/${productType}/${slug}`);

    if (Object.keys(router?.location?.query || {}).length > 0) {
      Object.keys(router.location.query).forEach((paramKey) => {
        if (queryParamsToRetain.has(paramKey) && router.location.query[paramKey]) {
          redirectUrl.addQueryParam(paramKey, router.location.query[paramKey]);
        } else if (paramKey === 'itemId') {
          redirectUrl.addQueryParam('deepLinkItemId', router.location.query[paramKey]);
        }
      });
    }

    redirectClientOrServer(redirectUrl.toString());
  }
}

export default useEnterpriseMiniModalRedirect;
